import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Check, CheckCircle2, Circle, Clock, Flame, Search, Users } from "lucide-react";
import { toast } from "sonner";

import { fetchClinicTasks, updateTask } from "@/lib/api/tasks";
import { fetchInternalChatPartners } from "@/lib/api/users";
import { ApiError } from "@/lib/api/client";
import { getAuthUser } from "@/lib/api/token-store";
import { subscribeUserThreadEvents } from "@/lib/realtime/chat-realtime-service";
import { requestOpenThreadMessage } from "@/lib/notifications/notification-navigation";
import {
  getTaskCounts,
  refreshTaskCounts,
  subscribeTaskCounts,
} from "@/lib/stores/task-counts-store";
import { SnoozeMenu } from "@/components/messages/snooze-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { setUserPreferences } from "@/lib/stores/user-preference-store";
import { cn } from "@/lib/utils";
import { DraggableTaskCard, TaskBoardCard } from "./task-board-card";

const UNASSIGNED = "unassigned";

const COLUMNS = [
  { id: "todo", label: "To do", icon: Circle, accent: "text-slate-500 dark:text-slate-400" },
  { id: "snoozed", label: "Snoozed", icon: Clock, accent: "text-amber-600 dark:text-amber-400" },
  { id: "done", label: "Done", icon: CheckCircle2, accent: "text-emerald-600 dark:text-emerald-400" },
];
const COLUMN_IDS = COLUMNS.map((c) => c.id);

function taskDueMs(task) {
  if (!task?.dueAt) return null;
  const t = new Date(task.dueAt).getTime();
  return Number.isNaN(t) ? null : t;
}

function laneOf(task) {
  return String(task?.assigneeId || "").trim() || UNASSIGNED;
}

/** A task is "snoozed" when it's an open task whose due date is still in the future. */
function columnOf(task, nowMs) {
  if (task?.status === "done") return "done";
  const due = taskDueMs(task);
  if (due != null && due > nowMs) return "snoozed";
  return "todo";
}

/** Overdue/oldest-due first, then by age — the same priority order as the list view. */
function priorityCompare(a, b) {
  const dueA = taskDueMs(a);
  const dueB = taskDueMs(b);
  if (dueA != null && dueB != null) return dueA - dueB;
  if (dueA != null) return -1;
  if (dueB != null) return 1;
  return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
}

/** A directory person's display name: first + last, then displayName, then id. */
function partnerName(p) {
  const composed = [p?.firstName, p?.lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  if (composed) return composed;
  const display = String(p?.displayName || "").trim();
  return display || String(p?.id || "").trim() || "Unknown";
}

/**
 * The "show these people as lanes" picker: a searchable checkbox directory. Pinning a teammate
 * keeps their (possibly empty) lane on the board so it stays a drop target for assignment.
 */
function LanePeoplePicker({ people, pinnedIds, onToggle }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const pinnedSet = new Set(pinnedIds);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? people.filter(
        (p) => p.name.toLowerCase().includes(q) || p.userType.toLowerCase().includes(q),
      )
    : people;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Choose teammates to always show as a lane"
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-muted-foreground shadow-sm transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Users className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
          People
          {pinnedIds.length ? (
            <span className="rounded-full bg-indigo-600 px-1.5 text-[10px] font-bold leading-[16px] text-white dark:bg-indigo-500">
              {pinnedIds.length}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="border-b border-border p-2">
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search teammates…"
              aria-label="Search teammates"
              className="h-7 w-full bg-transparent text-xs focus-visible:outline-none"
            />
          </div>
        </div>
        <ScrollArea viewportClassName="max-h-64 p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-center text-[11px] text-muted-foreground">No teammates found.</p>
          ) : (
            filtered.map((p) => {
              const checked = pinnedSet.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onToggle(p.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      checked
                        ? "border-indigo-600 bg-indigo-600 text-white dark:border-indigo-500 dark:bg-indigo-500"
                        : "border-muted-foreground/40",
                    )}
                    aria-hidden
                  >
                    {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{p.name}</span>
                  {p.userType ? (
                    <span className="shrink-0 text-[10px] capitalize text-muted-foreground">{p.userType}</span>
                  ) : null}
                </button>
              );
            })
          )}
        </ScrollArea>
        <p className="border-t border-border bg-muted/50 px-2.5 py-2 text-[10px] leading-snug text-muted-foreground">
          Pinned people always show a lane. You and anyone with tasks appear automatically.
        </p>
      </PopoverContent>
    </Popover>
  );
}

/** One droppable lane × column cell. */
function BoardCell({ laneId, column, tasks, nowMs, groups, onToggle, onClaim, onOpenThread }) {
  const { setNodeRef, isOver } = useDroppable({ id: `cell:${laneId}:${column}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[92px] border-b border-r border-border/60 p-2 transition-colors",
        isOver ? "bg-indigo-50/70 dark:bg-indigo-950/30" : "bg-muted/10",
      )}
    >
      <div className="flex flex-col gap-2">
        {tasks.map((task) => (
          <DraggableTaskCard
            key={task.id}
            task={task}
            nowMs={nowMs}
            groups={groups}
            onToggle={onToggle}
            onClaim={onClaim}
            onOpenThread={onOpenThread}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The swim-lane task board: lanes are teammates (Unassigned + You + the rest), columns are the
 * task lifecycle (To do / Snoozed / Done) over every board-relevant task across the clinic.
 * Dragging a card into a lane assigns/claims/releases it; dragging across columns snoozes or
 * completes it; a drop can do both at once. The Snoozed column header doubles as the snooze
 * preset picker (a snooze just sets the task's due date).
 *
 * The clinic-wide set is merged client-side from the existing smart views — there is no backend
 * in this repo, so a single GET /api/tasks?view=board endpoint (mirroring fetchBoardThreads)
 * isn't available; the fan-out below is the stand-in.
 */
export function TaskBoard({ groups = [], canManage = true }) {
  const me = String(getAuthUser()?.id || "").trim();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState(() => getTaskCounts());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [activeTaskId, setActiveTaskId] = useState(null);
  // While picking a snooze `until`: { taskId, lane: { patch, optimistic }, wasDone }.
  const [snoozePicker, setSnoozePicker] = useState(null);
  const [directory, setDirectory] = useState([]); // [{ id, name, userType }] of assignable staff
  const pinnedIds = useUserPreferences().tasksBoardVisibleAssigneeIds;
  const refreshTimerRef = useRef(null);
  const fetchSeqRef = useRef(0);

  useEffect(() => subscribeTaskCounts(setCounts), []);
  useEffect(() => {
    // Re-seed immediately: state survives an Activity freeze, so on thaw the snooze-lane /
    // due classification must not run against the freeze-time clock for up to 30s.
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // The assignable-staff directory powers the People picker (so idle teammates can be pinned as
  // lanes). Server-filtered to eligible user types; degrade to teammates-with-tasks if it fails.
  useEffect(() => {
    let cancelled = false;
    fetchInternalChatPartners()
      .then((res) => {
        if (cancelled) return;
        const items = (res?.items || [])
          .map((p) => ({
            id: String(p?.id || "").trim(),
            name: partnerName(p),
            userType: String(p?.userType || "").trim(),
          }))
          .filter((p) => p.id);
        setDirectory(items);
      })
      .catch(() => {
        if (!cancelled) setDirectory([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadBoard = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const teammateIds = (getTaskCounts()?.teammates || [])
        .map((t) => String(t?.id || "").trim())
        .filter(Boolean);
      const res = await fetchClinicTasks({ assigneeIds: teammateIds });
      if (fetchSeqRef.current !== seq) return;
      setTasks(res.tasks);
      setError("");
    } catch (e) {
      if (fetchSeqRef.current !== seq) return;
      setError(e instanceof Error ? e.message : "Could not load the board");
    } finally {
      if (fetchSeqRef.current === seq) setLoading(false);
    }
  }, []);
  const loadRef = useRef(loadBoard);
  loadRef.current = loadBoard;

  // Reload on mount and whenever the teammate roster changes (so each person's lane is fetched).
  const teammateKey = (counts?.teammates || [])
    .map((t) => String(t?.id || "").trim())
    .filter(Boolean)
    .sort()
    .join(",");
  useEffect(() => {
    void loadBoard();
  }, [teammateKey, loadBoard]);

  // Realtime: any task event reconciles the board (debounced like the list refresh).
  useEffect(() => {
    return subscribeUserThreadEvents((detail) => {
      if (!String(detail?.action || "").startsWith("task")) return;
      if (refreshTimerRef.current) return;
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        void loadRef.current();
      }, 500);
    });
  }, []);
  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    [],
  );

  // Display-name lookup for any assignable id (staff directory ∪ teammates ∪ task assignees).
  const nameById = useMemo(() => {
    const m = new Map();
    for (const p of directory) if (p.id) m.set(p.id, p.name);
    for (const t of counts?.teammates || []) {
      const id = String(t?.id || "").trim();
      if (id && !m.has(id)) m.set(id, String(t?.displayName || "").trim());
    }
    for (const task of tasks) {
      const id = String(task.assigneeId || "").trim();
      if (id && !m.has(id)) m.set(id, String(task.assignee?.displayName || "").trim());
    }
    return m;
  }, [directory, counts, tasks]);

  // Lanes: Unassigned, You, then teammates with tasks plus any the user pinned (always shown).
  const lanes = useMemo(() => {
    const ids = new Set();
    for (const t of counts?.teammates || []) {
      const id = String(t?.id || "").trim();
      if (id) ids.add(id);
    }
    for (const task of tasks) {
      const id = String(task.assigneeId || "").trim();
      if (id) ids.add(id);
    }
    for (const id of pinnedIds) {
      const s = String(id || "").trim();
      if (s) ids.add(s);
    }
    if (me) ids.delete(me);
    const others = [...ids]
      .map((id) => ({ id, name: nameById.get(id) || "Teammate" }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [
      { id: UNASSIGNED, name: "Unassigned" },
      ...(me ? [{ id: me, name: "You", isMe: true }] : []),
      ...others,
    ];
  }, [counts, tasks, pinnedIds, me, nameById]);

  // People offered in the lane picker: the staff directory ∪ teammates with tasks (minus you).
  const pickerPeople = useMemo(() => {
    const m = new Map();
    for (const p of directory) if (p.id && p.id !== me) m.set(p.id, p);
    for (const t of counts?.teammates || []) {
      const id = String(t?.id || "").trim();
      if (id && id !== me && !m.has(id)) {
        m.set(id, { id, name: String(t?.displayName || "").trim() || id, userType: "" });
      }
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [directory, counts, me]);

  const togglePinnedLane = useCallback(
    (id) => {
      const s = String(id || "").trim();
      if (!s) return;
      const next = pinnedIds.includes(s) ? pinnedIds.filter((x) => x !== s) : [...pinnedIds, s];
      setUserPreferences({ tasksBoardVisibleAssigneeIds: next });
    },
    [pinnedIds],
  );

  const laneNameById = useMemo(() => {
    const map = new Map();
    for (const lane of lanes) map.set(lane.id, lane.name);
    return map;
  }, [lanes]);

  // cells[laneId][columnId] = sorted Task[].
  const cells = useMemo(() => {
    const map = {};
    for (const lane of lanes) map[lane.id] = { todo: [], snoozed: [], done: [] };
    for (const task of tasks) {
      const laneId = laneOf(task);
      if (!map[laneId]) map[laneId] = { todo: [], snoozed: [], done: [] };
      map[laneId][columnOf(task, nowMs)].push(task);
    }
    for (const laneId of Object.keys(map)) {
      for (const col of COLUMN_IDS) map[laneId][col].sort(priorityCompare);
    }
    return map;
  }, [tasks, lanes, nowMs]);

  const unclaimedCount = cells[UNASSIGNED]?.todo.length || 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Apply a built patch optimistically, then PATCH the task; reconcile/rollback on the result. */
  const commit = useCallback((taskId, patch, optimistic) => {
    if (!taskId || Object.keys(patch).length === 0) return;
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...optimistic } : t)));
    void (async () => {
      try {
        const res = await updateTask({ taskId, ...patch });
        if (res.task) {
          setTasks((prev) =>
            prev.map((t) => (t.id === taskId ? { ...t, ...res.task, thread: t.thread } : t)),
          );
        }
        void refreshTaskCounts();
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          toast.error("Someone else just claimed this task.");
        } else {
          toast.error(e instanceof Error ? e.message : "Could not update task");
        }
        void loadRef.current();
      }
    })();
  }, []);

  // Build the assignee (lane) part of a move.
  const laneChange = useCallback(
    (task, sourceLane, targetLane) => {
      if (targetLane === sourceLane) return { patch: {}, optimistic: {} };
      if (targetLane === UNASSIGNED) {
        return { patch: { assigneeId: "" }, optimistic: { assigneeId: "", assignee: null } };
      }
      if (targetLane === me) {
        // Claim from the pool (no reassign — a lost race surfaces as 409); take over otherwise.
        const patch = { assigneeId: me, ...(sourceLane !== UNASSIGNED ? { reassign: true } : {}) };
        return { patch, optimistic: { assigneeId: me, assignee: null } };
      }
      return {
        patch: { assigneeId: targetLane, reassign: true },
        optimistic: {
          assigneeId: targetLane,
          assignee: { id: targetLane, displayName: laneNameById.get(targetLane) || "" },
        },
      };
    },
    [me, laneNameById],
  );

  const applyMove = useCallback(
    (task, sourceLane, sourceCol, targetLane, targetCol) => {
      const lane = laneChange(task, sourceLane, targetLane);

      // Moving into Snoozed needs an `until` — defer to the preset picker, carrying the lane part.
      if (targetCol === "snoozed" && sourceCol !== "snoozed") {
        setSnoozePicker({ taskId: task.id, lane, wasDone: task.status === "done" });
        return;
      }

      const col = { patch: {}, optimistic: {} };
      if (targetCol !== sourceCol) {
        if (targetCol === "done") {
          col.patch = { status: "done" };
          col.optimistic = { status: "done", doneAt: new Date().toISOString() };
        } else if (targetCol === "todo") {
          if (task.status === "done") {
            col.patch.status = "todo";
            col.optimistic.status = "todo";
            col.optimistic.doneAt = null;
          }
          if (sourceCol === "snoozed") {
            col.patch.dueAt = "";
            col.optimistic.dueAt = null;
          }
        }
      }
      commit(task.id, { ...lane.patch, ...col.patch }, { ...lane.optimistic, ...col.optimistic });
    },
    [laneChange, commit],
  );

  const handleDragEnd = ({ active, over }) => {
    setActiveTaskId(null);
    // RBAC tasks:view — the board renders but drops never mutate.
    if (!canManage) return;
    const taskId = String(active?.id || "").trim();
    if (!taskId || !over) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const overId = String(over.id || "").trim();
    let targetLane;
    let targetCol;
    if (overId.startsWith("cell:")) {
      const rest = overId.slice("cell:".length);
      const sep = rest.lastIndexOf(":");
      targetLane = rest.slice(0, sep);
      targetCol = rest.slice(sep + 1);
    } else {
      const overTask = tasks.find((t) => t.id === overId);
      if (!overTask) return;
      targetLane = laneOf(overTask);
      targetCol = columnOf(overTask, nowMs);
    }
    if (!targetLane || !COLUMN_IDS.includes(targetCol)) return;

    const sourceLane = laneOf(task);
    const sourceCol = columnOf(task, nowMs);
    if (sourceLane === targetLane && sourceCol === targetCol) return;
    applyMove(task, sourceLane, sourceCol, targetLane, targetCol);
  };

  const confirmSnooze = (until) => {
    const picker = snoozePicker;
    setSnoozePicker(null);
    if (!picker) return;
    const dueIso = until instanceof Date ? until.toISOString() : String(until || "");
    if (!dueIso) return;
    const patch = { ...picker.lane.patch, dueAt: dueIso };
    const optimistic = { ...picker.lane.optimistic, dueAt: dueIso };
    if (picker.wasDone) {
      patch.status = "todo";
      optimistic.status = "todo";
      optimistic.doneAt = null;
    }
    commit(picker.taskId, patch, optimistic);
  };

  const onToggle = useCallback(
    (taskId, nextDone) => {
      commit(
        taskId,
        { status: nextDone ? "done" : "todo" },
        nextDone
          ? { status: "done", doneAt: new Date().toISOString() }
          : { status: "todo", doneAt: null },
      );
    },
    [commit],
  );

  const onClaim = useCallback(
    (taskId) => {
      if (!me) return;
      commit(taskId, { assigneeId: me }, { assigneeId: me, assignee: null });
    },
    [commit, me],
  );

  const onOpenThread = useCallback((threadId) => {
    const id = String(threadId || "").trim();
    if (id) requestOpenThreadMessage({ threadId: id });
  }, []);

  const activeTask = activeTaskId ? tasks.find((t) => t.id === activeTaskId) : null;
  const gridTemplateColumns = `minmax(150px, 200px) repeat(${COLUMNS.length}, minmax(232px, 1fr))`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-4 pt-3">
        <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-[3px] text-[11px] font-semibold leading-none text-rose-600 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-300">
          <Flame className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
          {unclaimedCount} unclaimed
        </span>
        {error ? <span className="text-[11px] text-destructive">{error}</span> : null}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <span className="hidden text-[11px] text-muted-foreground lg:block">
            Drag into a lane to assign · across columns to snooze or complete.
          </span>
          <LanePeoplePicker people={pickerPeople} pinnedIds={pinnedIds} onToggle={togglePinnedLane} />
        </div>
      </div>

      {loading && tasks.length === 0 ? (
        <div className="grid min-h-0 flex-1 gap-2 p-4" style={{ gridTemplateColumns }}>
          {Array.from({ length: (COLUMNS.length + 1) * 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={({ active }) => setActiveTaskId(String(active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveTaskId(null)}
        >
          <ScrollArea orientation="both" className="min-h-0 flex-1" viewportClassName="p-4">
            <div
              className="grid w-full overflow-hidden rounded-xl border-l border-t border-border/60"
              style={{ gridTemplateColumns }}
            >
              {/* Header row */}
              <div className="sticky left-0 top-0 z-30 border-b border-r border-border/60 bg-background" />
              {COLUMNS.map((col) => {
                const Icon = col.icon;
                const headerEl = (
                  <div
                    className={cn(
                      "sticky top-0 z-20 flex items-center gap-1.5 border-b border-r border-border/60 bg-background px-3 py-2",
                      col.id === "snoozed" ? "cursor-pointer select-none" : "",
                    )}
                  >
                    <Icon className={cn("h-3.5 w-3.5 shrink-0", col.accent)} strokeWidth={2.5} aria-hidden />
                    <span className="text-xs font-bold text-foreground">{col.label}</span>
                  </div>
                );
                return col.id === "snoozed" ? (
                  <SnoozeMenu
                    key={col.id}
                    open={Boolean(snoozePicker)}
                    onOpenChange={(open) => {
                      if (!open) setSnoozePicker(null);
                    }}
                    align="center"
                    onSnooze={confirmSnooze}
                  >
                    {headerEl}
                  </SnoozeMenu>
                ) : (
                  <Fragment key={col.id}>{headerEl}</Fragment>
                );
              })}

              {/* Lane rows */}
              {lanes.map((lane) => (
                <Fragment key={lane.id}>
                  <div className="sticky left-0 z-10 flex items-center border-b border-r border-border/60 bg-background px-3 py-2">
                    <span
                      className={cn(
                        "truncate text-[11px] font-bold uppercase tracking-wide",
                        lane.isMe ? "text-indigo-600 dark:text-indigo-400" : "text-muted-foreground",
                      )}
                    >
                      {lane.name}
                    </span>
                  </div>
                  {COLUMNS.map((col) => (
                    <BoardCell
                      key={col.id}
                      laneId={lane.id}
                      column={col.id}
                      tasks={cells[lane.id]?.[col.id] || []}
                      nowMs={nowMs}
                      groups={groups}
                      onToggle={onToggle}
                      onClaim={onClaim}
                      onOpenThread={onOpenThread}
                    />
                  ))}
                </Fragment>
              ))}
            </div>
          </ScrollArea>

          <DragOverlay>
            {activeTask ? <TaskBoardCard task={activeTask} nowMs={nowMs} groups={groups} isDragging /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
